import {
  Avatar,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListSubheader,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Assignment as AssignmentIcon } from '@material-ui/icons';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Form } from 'react-final-form';
import _ from 'lodash';
import { useSnackbar } from '../Snackbar';
import {
  CREATE_TAG,
  DELETE_TAG,
  FETCH_TAG,
  FETCH_TAG_SUCCESS,
  UPDATE_TAG,
} from '../../actions';
import { doesIdExist, urlInvalidCharactersRegex } from '../../apis/utilities';
import ConfirmationDialog from '../dialogs/ConfirmationDialog';
import {
  Field,
  ItemsField,
  TextField,
  ContentField,
  required,
} from '../fields';
import { RouteLeavingGuard } from '../controls';

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: theme.spacing(1),
    minWidth: 240,
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: 200,
  },
  cardContent: {
    padding: 0,
  },
  section: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  subHeader: {
    marginTop: 10,
  },
  editor: {
    width: '100%',
    marginBottom: 16,
  },
  delete: {
    color: theme.palette.error.main,
  },
  close: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  edits: {
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      paddingLeft: theme.spacing(2),
    },
  },
}));

const initialValues = {
  type: 'Tag',
};

export default function Tag() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const tag = useSelector((state) => state.tags.tag, _.isEqual);
  const error = useSelector((state) => state.tags.error);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const classes = useStyles();
  const snackbar = useSnackbar();

  useEffect(() => {
    if (error) {
      snackbar.notify('error', error);
    }
  }, [error, snackbar]);

  useEffect(() => {
    if (id === 'new') {
      dispatch({
        type: FETCH_TAG_SUCCESS,
        payload: null,
      });
    } else {
      dispatch({
        type: FETCH_TAG,
        payload: id,
      });
    }
  }, [id, dispatch]);

  function handleDelete() {
    if (tag) {
      dispatch({
        type: DELETE_TAG,
        payload: encodeURIComponent(tag.identifier),
      });
    }
  }

  async function validate(values) {
    const errors = {};

    if (!tag) {
      if (values.identifier) {
        if (values.identifier === 'new') {
          errors.identifier = 'Invalid';
        }
        if (urlInvalidCharactersRegex.test(values.identifier)) {
          errors.identifier = 'Invalid characters';
        }
        const exists = await doesIdExist('tags', values.identifier);
        if (exists) {
          errors.identifier = 'Exists';
        }
      }
    }

    return errors;
  }

  function onSubmit(values) {
    if (tag) {
      dispatch({
        type: UPDATE_TAG,
        payload: values,
      });
    } else {
      dispatch({
        type: CREATE_TAG,
        payload: values,
      });
    }
  }

  return (
    <Form
      initialValues={tag || initialValues}
      validate={validate}
      onSubmit={onSubmit}
      render={({
        handleSubmit,
        form: { reset },
        submitting,
        dirty,
        pristine,
        values,
      }) => (
        <form onSubmit={handleSubmit}>
          <Helmet>
            <title>
              IR3 | Tag{values.identifier ? ` | ${values.identifier}` : ''}
            </title>
          </Helmet>
          <Paper className={classes.paper}>
            <CardHeader
              avatar={
                <Avatar aria-label="Collection" className={classes.avatar}>
                  <AssignmentIcon />
                </Avatar>
              }
              title={values.identifier}
            />
            <CardContent className={classes.cardContent}>
              {values.created && (
                <div className={classes.edits}>
                  <Typography variant="caption">
                    {`Created by ${values.created.userId} ${moment(
                      values.created.time
                    ).format('DD/MM/YYYY, HH:mm')}`}
                  </Typography>
                </div>
              )}
              {values.lastEdit && (
                <div className={classes.edits}>
                  <Typography variant="caption">
                    {`Last edited by ${values.lastEdit.userId} ${moment(
                      values.lastEdit.time
                    ).format('DD/MM/YYYY, HH:mm')}`}
                  </Typography>
                </div>
              )}
              <List>
                <ListSubheader disableSticky>Key Information</ListSubheader>
                <ListItem>
                  <div className={classes.section}>
                    <Field
                      name="identifier"
                      component={TextField}
                      label="Code"
                      className={classes.textField}
                      validate={required}
                      disabled={tag && 'identifier' in tag}
                    />
                  </div>
                </ListItem>
                <Divider />
                <ListSubheader disableSticky>Description</ListSubheader>
                <ListItem>
                  <Field
                    name="description"
                    component={ContentField}
                    className={classes.editor}
                  />
                </ListItem>
                <Divider />
                {!_.isEmpty(values.items) && (
                  <React.Fragment>
                    <ListSubheader disableSticky>Items</ListSubheader>
                    <React.Fragment>
                      {values.items && (
                        <Field name="items" component={ItemsField} />
                      )}
                    </React.Fragment>
                  </React.Fragment>
                )}
              </List>
            </CardContent>

            <CardActions disableSpacing>
              <Button
                color="primary"
                type="submit"
                disabled={pristine || submitting}
              >
                Save
              </Button>
              <Button
                color="primary"
                disabled={pristine || submitting}
                onClick={reset}
              >
                Cancel
              </Button>
              <Button
                className={classes.delete}
                onClick={() => setDeleteOpen(true)}
                disabled={tag === null}
              >
                Delete
              </Button>
            </CardActions>
          </Paper>
          <ConfirmationDialog
            action="Delete"
            open={deleteOpen}
            itemId={values.identifier}
            onOk={handleDelete}
            onCancel={() => setDeleteOpen(false)}
          />
          <RouteLeavingGuard when={dirty} />
        </form>
      )}
    />
  );
}
